import { useTheme } from "@mui/material/styles";
import {
  brand,
  brandGradient,
  canvas,
  glow,
  lightBrand,
  lightGlow,
  palette,
} from "../theme";

export const useCustomColors = () => {
  const { palette: muiPalette } = useTheme();
  const isDark = muiPalette.mode === "dark";
  const tone = isDark ? palette.dark : palette.light;
  const accent = isDark ? brand : lightBrand;
  const washes = isDark ? glow : lightGlow;

  return {
    isDark,
    background: {
      page: tone.page,
      /** Flat base behind the page atmosphere; never pure black. */
      canvas: isDark ? canvas.bottom : tone.page,
      gradient: isDark
        ? [
            `radial-gradient(circle at 20% 18%, rgba(160, 38, 32, 0.38) 0%, transparent 48%)`,
            `radial-gradient(circle at 80% 52%, rgba(176, 44, 36, 0.30) 0%, transparent 50%)`,
            `radial-gradient(circle at 50% 88%, rgba(160, 38, 32, 0.34) 0%, transparent 52%)`,
            `linear-gradient(180deg, ${canvas.top} 0%, ${canvas.upper} 28%, ${canvas.mid} 52%, ${canvas.lower} 72%, ${canvas.bottom} 100%)`,
          ].join(", ")
        : tone.page,
      bar: tone.bar,
      field: tone.field,
      rail: tone.rail,
      deep: tone.deep,
      panelSecondary: tone.panelSecondary,
    },
    text: {
      primary: tone.textPrimary,
      secondary: tone.textSecondary,
      disabled: tone.textDisabled,
      onGradient: isDark ? "#F3E4C3" : tone.textPrimary,
    },
    border: {
      /** Default container edge: deep red. */
      primary: tone.border,
      /** Premium container edge: gold. */
      accent: tone.borderAccent,
      strong: isDark ? `${brand.gold}73` : tone.borderAccent,
      hover: isDark ? `${brand.gold}A6` : accent.gold,
      focus: isDark ? brand.primaryBright : accent.gold,
      /** Quiet edge for dense chrome (table rows, list separators). */
      subtle: isDark ? "rgba(255, 255, 255, 0.10)" : "rgba(24, 16, 14, 0.08)",
    },
    card: {
      background: tone.panel,
      /** Panels: dark foundation lifting into deep red. */
      gradient: isDark
        ? `linear-gradient(145deg, ${tone.panel} 0%, ${tone.panelSecondary} 45%, ${brand.primaryMuted} 100%)`
        : tone.panel,
      /** Table headers and other tinted strips. */
      head: isDark
        ? `linear-gradient(180deg, ${brand.primaryActive} 0%, ${brand.primaryDark} 100%)`
        : tone.panelSecondary,
      border: tone.border,
      dialog: isDark ? tone.panel : tone.panel,
      dialogBody: isDark
        ? `linear-gradient(180deg, ${tone.page} 0%, ${brand.primaryDark} 100%)`
        : tone.panel,
      dialogFooter: isDark ? brand.primaryDark : tone.panelSecondary,
      dialogShadow: isDark
        ? `0 24px 70px rgba(0, 0, 0, 0.55), 0 0 26px ${glow.gold}`
        : `0 24px 70px rgba(45, 33, 26, 0.16), 0 0 26px ${washes.gold}`,
      shadow: isDark
        ? `0 12px 32px rgba(0, 0, 0, 0.45), 0 12px 32px ${glow.redSoft}`
        : "0 12px 32px rgba(24, 16, 14, 0.10)",
    },
    /* Gold carries the premium note on the dark canvas, but as small text on
       parchment it fails contrast, so light mode states headings in crimson
       and leaves gold to rules, rings and other decoration. */
    heading: {
      /** Headings that are gold in dark mode. */
      gold: isDark ? accent.gold : accent.primary,
      /** Headings that are plain body ink in dark mode. */
      plain: isDark ? tone.textPrimary : accent.primary,
    },
    footer: {
      /** The age notice and the copyright line. */
      note: isDark ? tone.textDisabled : tone.textSecondary,
      link: isDark ? tone.textSecondary : tone.textPrimary,
      linkHover: isDark ? accent.goldBright : accent.primary,
      supportHover: isDark ? accent.gold : accent.primary,
    },
    accent: {
      /** Deep red: primary actions, active states, selected surfaces. */
      primary: accent.primary,
      primaryHover: accent.primaryHover,
      primaryActive: accent.primaryActive,
      primarySoft: accent.primarySoft,
      /** Gold: money, prizes and premium emphasis. */
      gold: accent.gold,
      goldBright: accent.goldBright,
      goldSoft: accent.goldSoft,
      green: accent.success,
      cyan: accent.info,
      amber: accent.warning,
      danger: accent.error,
    },
    /** Translucent washes: selected rows, soft chips, hover fills. */
    tint: {
      soft: isDark ? "rgba(208, 52, 44, 0.18)" : "rgba(158, 27, 27, 0.08)",
      primary: isDark ? "rgba(208, 52, 44, 0.28)" : "rgba(158, 27, 27, 0.12)",
      primaryStrong: isDark ? "rgba(208, 52, 44, 0.45)" : "rgba(158, 27, 27, 0.18)",
      primaryHover: isDark ? "rgba(208, 52, 44, 0.38)" : "rgba(158, 27, 27, 0.16)",
      rowHover: isDark ? "rgba(208, 52, 44, 0.24)" : "#F4E7C5",
      gold: isDark ? "rgba(212, 175, 55, 0.12)" : "rgba(184, 138, 53, 0.12)",
      goldStrong: isDark ? "rgba(212, 175, 55, 0.24)" : "rgba(184, 138, 53, 0.20)",
      green: isDark ? "rgba(67, 197, 158, 0.12)" : "rgba(38, 115, 77, 0.12)",
      scrim: isDark ? "rgba(28, 6, 5, 0.66)" : "rgba(45, 33, 26, 0.28)",
      /** Image overlays: keep artwork readable under text. */
      imageScrim: "rgba(43, 11, 9, 0.94)",
    },
    glow: {
      primary: `0 0 18px ${washes.red}`,
      gold: `0 0 24px ${washes.gold}`,
      card: isDark
        ? `0 14px 36px rgba(28, 6, 5, 0.55), 0 0 26px ${glow.redSoft}`
        : `0 14px 36px rgba(45, 33, 26, 0.10), 0 0 26px ${washes.redSoft}`,
      /** Top-up pill: red ring outside, metallic gold edge inside. */
      topUpRing: [
        "inset 0 1px 0 rgba(255, 252, 226, 0.95)",
        "inset 0 -2px 3px rgba(59, 17, 18, 0.10)",
        "0 0 0 1px rgba(122, 90, 20, 0.55)",
        "0 0 0 4px rgba(99, 25, 26, 0.30)",
        "0 0 0 5px rgba(212, 175, 55, 0.20)",
        "0 8px 20px rgba(0, 0, 0, 0.32)",
      ].join(", "),
      topUpRingHover: [
        "inset 0 1px 0 rgba(255, 255, 255, 1)",
        "inset 0 -2px 3px rgba(59, 17, 18, 0.12)",
        "0 0 0 1px rgba(212, 175, 55, 0.62)",
        "0 0 0 4px rgba(99, 25, 26, 0.38)",
        "0 0 0 6px rgba(232, 200, 90, 0.24)",
        "0 0 16px rgba(212, 175, 55, 0.16)",
        "0 10px 24px rgba(0, 0, 0, 0.36)",
      ].join(", "),
      /** Ring that lifts the top-up pill off the wallet card. */
      topUp: `0 0 0 6px ${accent.primaryActive}`,
      /** Edge light: top highlight + bottom shade reads as a raised surface. */
      cardEdge:
        "inset 0 1px 0 rgba(255, 255, 255, 0.16), inset 0 -1px 0 rgba(0, 0, 0, 0.45)",
      /** Inner red atmosphere for large panels. */
      cardInner: `inset 0 0 90px ${washes.redSoft}`,
    },
    /** Bet board: deep red cards, cream labels, gold odds, near-black inputs. */
    bet: {
      card: isDark
        ? "radial-gradient(circle at 50% 8%, #6E1210 0%, #4A0D0C 46%, #2A0708 100%)"
        : tone.panel,
      row: isDark
        ? "linear-gradient(180deg, rgba(120, 20, 18, 0.55) 0%, rgba(58, 10, 10, 0.75) 100%)"
        : tone.panelSecondary,
      rowActive: isDark
        ? "linear-gradient(180deg, rgba(168, 36, 25, 0.62) 0%, rgba(86, 14, 12, 0.85) 100%)"
        : "#F4E7C5",
      title: isDark ? "#F7E2B0" : tone.textPrimary,
      label: isDark ? "#FBEFD8" : tone.textSecondary,
      odds: isDark ? "#E8C85A" : accent.gold,
      input: isDark ? "#1A0807" : tone.field,
      inputText: isDark ? "#F7E2B0" : tone.textPrimary,
      /** Warm top sheen + deep vignette: the card reads as lit from above. */
      cardGlow: [
        "inset 0 1px 0 rgba(255, 224, 160, 0.40)",
        "inset 0 26px 48px rgba(255, 138, 92, 0.10)",
        "inset 0 -30px 60px rgba(0, 0, 0, 0.45)",
        "0 16px 40px rgba(0, 0, 0, 0.45)",
      ].join(", "),
      /** Row bevel: light catches the top edge, shade pools at the bottom. */
      rowGlow: [
        "inset 0 1px 0 rgba(255, 226, 176, 0.18)",
        "inset 0 -8px 16px rgba(0, 0, 0, 0.32)",
      ].join(", "),
      rowGlowActive: [
        "inset 0 1px 0 rgba(255, 236, 196, 0.30)",
        "inset 0 -8px 16px rgba(0, 0, 0, 0.30)",
        "0 0 18px rgba(232, 200, 90, 0.18)",
      ].join(", "),
      /** Recessed well for the amount field. */
      inputGlow:
        "inset 0 2px 6px rgba(0, 0, 0, 0.65), inset 0 -1px 0 rgba(255, 226, 176, 0.10)",
      oddsGlow: "0 0 10px rgba(232, 200, 90, 0.35)",
    },
    /** Winning-results table: gold frame, red ribbons, gold number badges. */
    table: {
      surface: isDark
        ? `linear-gradient(145deg, ${brand.primarySoft} 0%, ${tone.panel} 55%, ${brand.primaryMuted} 100%)`
        : tone.panel,
      border: isDark ? "rgba(212, 175, 55, 0.55)" : tone.border,
      frame: isDark
        ? "inset 0 0 0 1px rgba(212, 175, 55, 0.10), 0 10px 30px rgba(0, 0, 0, 0.35)"
        : "0 10px 30px rgba(24, 16, 14, 0.10)",
      // Opaque: the sticky first column paints over the scrolling cells.
      headBg: isDark ? "#100909" : accent.primary,
      headText: isDark ? "#DCC994" : "#FFFDF9",
      headDivider: isDark ? "rgba(212, 175, 55, 0.24)" : tone.border,
      rowOdd: isDark ? "#1B1211" : tone.panel,
      rowEven: isDark ? "#221715" : tone.panelSecondary,
      rowDivider: isDark ? "rgba(212, 175, 55, 0.13)" : tone.border,
      rowHover:
        isDark ? "linear-gradient(90deg, rgba(99, 25, 26, 0.20), rgba(212, 175, 55, 0.05))" : "#F4E7C5",
      ribbon: isDark
        ? `linear-gradient(90deg, ${brand.primaryHover} 0%, #63191A 55%, #3D1012 100%)`
        : `linear-gradient(90deg, ${lightBrand.primary} 0%, ${lightBrand.primaryHover} 100%)`,
      ribbonEdge: isDark ? "rgba(212, 175, 55, 0.45)" : "rgba(184, 138, 53, 0.45)",
      numberBg: isDark ? "radial-gradient(circle, #1A1111 0%, #0C0909 70%)" : tone.panel,
      numberBorder: accent.gold,
      /* The badge sits on crimson and the circle on parchment, so they cannot
         share one ink: the circle wants espresso, the badge wants soft white. */
      ribbonText: isDark ? "#FFF8E8" : "#FFFDF9",
      numberText: isDark ? "#FFF8E8" : tone.textPrimary,
      /* Gold haloes read as glow against the dark canvas and as grime against
         parchment, so light mode gets a plain drop shadow instead. */
      numberGlow: isDark
        ? "0 0 0 2px rgba(99, 25, 26, 0.30), 0 0 10px rgba(212, 175, 55, 0.24), inset 0 0 8px rgba(212, 175, 55, 0.08)"
        : "0 1px 3px rgba(45, 33, 26, 0.12)",
      numberGlowHover: isDark
        ? "0 0 0 2px rgba(99, 25, 26, 0.40), 0 0 16px rgba(212, 175, 55, 0.42), inset 0 0 10px rgba(212, 175, 55, 0.14)"
        : "0 2px 6px rgba(45, 33, 26, 0.18)",
      /** Cast by the sticky first column over the cells scrolling beneath it. */
      stickyShadow: isDark
        ? "8px 0 12px -8px rgba(0, 0, 0, 0.75)"
        : "8px 0 12px -8px rgba(45, 33, 26, 0.18)",
    },
    gradient: {
      brand: isDark ? brandGradient : accent.primary,
      brandGlow: `0 8px 24px ${washes.red}`,
      /** Hairline that carries the brand across full-width edges. */
      divider: `linear-gradient(90deg, transparent, ${accent.primary}, ${accent.gold}, ${accent.primary}, transparent)`,
      drawer: isDark
        ? `linear-gradient(180deg, ${brand.primaryDark} 0%, ${tone.page} 50%, ${brand.primaryMuted} 100%)`
        : `linear-gradient(180deg, ${tone.bar} 0%, ${tone.page} 100%)`,
      gold: `linear-gradient(135deg, ${accent.goldHover} 0%, ${accent.gold} 55%, ${accent.goldSoft} 100%)`,
      /** Champagne-gold body for premium pills. */
      goldPill:
        "linear-gradient(180deg, #FBEFC4 0%, #F2DC96 34%, #E4C36A 62%, #D4AC52 100%)",
      /** Glossy red gem for the icon well. */
      redGem:
        "radial-gradient(circle at 34% 26%, #E0584C 0%, #B92C22 38%, #7A1A14 74%, #4A0F0C 100%)",
      /** Brushed metal, not flat yellow: for gold edges and rims. */
      goldMetal:
        "linear-gradient(110deg, #7A5A14 0%, #D4AF37 22%, #FFF0B0 46%, #F3DA75 54%, #B9943E 72%, #E8C85A 100%)",
      /** Metallic gold for high-value amounts; pair with the shine keyframes. */
      goldShine: `linear-gradient(110deg, ${brand.goldDark} 0%, ${brand.gold} 25%, ${brand.goldHighlight} 45%, ${brand.goldBright} 52%, ${brand.gold} 65%, ${brand.goldDark} 100%)`,
      wallet: isDark
        ? `radial-gradient(circle at 86% 14%, rgba(212, 175, 55, 0.08) 0%, transparent 34%), radial-gradient(circle at 16% 100%, rgba(99, 25, 26, 0.26) 0%, transparent 52%), linear-gradient(145deg, ${canvas.top} 0%, #3F0A08 58%, ${canvas.mid} 100%)`
        : `linear-gradient(145deg, ${tone.panel} 0%, ${tone.panelSecondary} 100%)`,
      hero: isDark
        ? `linear-gradient(135deg, ${tone.page} 0%, ${brand.primaryDark} 32%, ${brand.primaryMuted} 62%, ${brand.primary} 100%)`
        : `linear-gradient(135deg, ${tone.panel} 0%, ${tone.panelSecondary} 100%)`,
      dialogHeader: isDark
        ? `linear-gradient(110deg, ${brand.primaryDark} 0%, ${brand.primaryActive} 55%, ${brand.primary} 100%)`
        : accent.primary,
      header: isDark
        ? `linear-gradient(180deg, ${brand.primaryActive} 0%, ${brand.primaryDark} 55%, ${tone.page} 100%)`
        : `linear-gradient(180deg, ${tone.bar} 0%, ${tone.page} 100%)`,
    },
  };
};
